import React, { useState } from 'react';
import { Search, Package, MessageCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { businessInfo } from '../mock.js';

const Products = () => {
  const [activeTab, setActiveTab] = useState('jcb');
  const [searchQuery, setSearchQuery] = useState('');

  const handleWhatsAppEnquiry = (categoryName) => {
    const message = encodeURIComponent(`Hi, I need information about ${categoryName}.`);
    window.open(`https://wa.me/${businessInfo.whatsapp}?text=${message}`, '_blank');
  };

  const jcbParts = [
    { name: 'Engine Parts', desc: 'Pistons, rings, liners, gaskets, oil pumps' },
    { name: 'Hydraulic Parts', desc: 'Pumps, cylinders, valves, hoses, seals' },
    { name: 'Electrical & Sensors', desc: 'Alternators, starters, sensors, switches' },
    { name: 'Transmission & Axle', desc: 'Clutch plates, gears, axle shafts, bearings' },
    { name: 'Wear Parts', desc: 'Bucket teeth, bushes, pins, cutting edges' },
    { name: 'Filters & Service Kits', desc: 'Oil, fuel, air, hydraulic filters' }
  ];

  const forkliftParts = [
    { name: 'Engine & Powertrain', desc: 'For diesel, electric, and LPG forklifts' },
    { name: 'Hydraulic Components', desc: 'Lift cylinders, pumps, control valves' },
    { name: 'Brake System', desc: 'Brake pads, shoes, master cylinder, lines' },
    { name: 'Steering & Suspension', desc: 'Steering components, shock absorbers' },
    { name: 'Electrical & Controllers', desc: 'Motors, controllers, batteries, cables' },
    { name: 'Mast, Chains & Rollers', desc: 'Mast sections, lift chains, rollers' },
    { name: 'Tyres & Wear Parts', desc: 'Solid tyres, pneumatic tyres, fork tips' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <section className="bg-gradient-to-br from-gray-50 to-gray-100 py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Spare Parts <span className="text-[#FF6B7A]">Catalogue</span>
            </h1>
            <p className="text-lg text-gray-700 mb-8">
              Browse our categories and send enquiry for availability and pricing
            </p>

            <div className="relative max-w-2xl mx-auto">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by part category..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 pr-4 py-6 text-base border-2"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="flex justify-center mb-12">
            <div className="inline-flex rounded-lg bg-gray-100 p-1">
              <button
                onClick={() => setActiveTab('jcb')}
                className={`px-6 py-3 rounded-md text-base font-medium transition-all ${
                  activeTab === 'jcb'
                    ? 'bg-[#FF6B7A] text-white shadow'
                    : 'text-gray-700 hover:text-gray-900'
                }`}
              >
                JCB Parts
              </button>
              <button
                onClick={() => setActiveTab('forklift')}
                className={`px-6 py-3 rounded-md text-base font-medium transition-all ${
                  activeTab === 'forklift'
                    ? 'bg-[#00C9FF] text-white shadow'
                    : 'text-gray-700 hover:text-gray-900'
                }`}
              >
                Forklift Parts
              </button>
            </div>
          </div>

          {/* JCB Parts */}
          {activeTab === 'jcb' && (
            <div>
              <div className="mb-8 text-center">
                <h2 className="text-3xl font-bold text-gray-900 mb-3">JCB Backhoe Loader Spare Parts</h2>
                <p className="text-gray-600 mb-4">Complete range of spare parts for JCB backhoe loaders</p>
                <div className="flex flex-wrap gap-2 justify-center">
                  <span className="bg-[#FF6B7A]/10 text-[#FF6B7A] px-3 py-1 rounded-full text-sm font-medium">JCB 3DX</span>
                  <span className="bg-[#FF6B7A]/10 text-[#FF6B7A] px-3 py-1 rounded-full text-sm font-medium">JCB 3CX</span>
                  <span className="bg-[#FF6B7A]/10 text-[#FF6B7A] px-3 py-1 rounded-full text-sm font-medium">JCB 4DX</span>
                  <span className="bg-[#FF6B7A]/10 text-[#FF6B7A] px-3 py-1 rounded-full text-sm font-medium">JCB 2DX</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {jcbParts.map((part, index) => (
                  <Card key={index} className="hover:shadow-xl transition-all border-2 hover:border-[#FF6B7A]">
                    <CardContent className="p-6">
                      <div className="bg-gradient-to-br from-[#FF6B7A]/10 to-[#FF6B7A]/5 p-4 rounded-lg mb-4 flex items-center justify-center">
                        <Package className="h-12 w-12 text-[#FF6B7A]" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">{part.name}</h3>
                      <p className="text-sm text-gray-600 mb-4">{part.desc}</p>
                      <div className="flex gap-2">
                        <Button 
                          onClick={() => handleWhatsAppEnquiry(part.name)}
                          className="flex-1 bg-[#25D366] hover:bg-[#20BD5A] text-white"
                          size="sm"
                        >
                          <MessageCircle className="mr-2 h-4 w-4" />
                          WhatsApp
                        </Button>
                        <Button 
                          variant="outline"
                          className="border-[#FF6B7A] text-[#FF6B7A] hover:bg-[#FF6B7A] hover:text-white"
                          size="sm"
                        >
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Forklift Parts */}
          {activeTab === 'forklift' && (
            <div>
              <div className="mb-8 text-center">
                <h2 className="text-3xl font-bold text-gray-900 mb-3">Forklift Spare Parts</h2>
                <p className="text-gray-600 mb-4">Comprehensive spare parts for all types of forklifts</p>
                <div className="flex flex-wrap gap-2 justify-center">
                  <span className="bg-[#00C9FF]/10 text-[#00C9FF] px-3 py-1 rounded-full text-sm font-medium">Diesel Forklift</span>
                  <span className="bg-[#00C9FF]/10 text-[#00C9FF] px-3 py-1 rounded-full text-sm font-medium">Electric Forklift</span>
                  <span className="bg-[#00C9FF]/10 text-[#00C9FF] px-3 py-1 rounded-full text-sm font-medium">LPG Forklift</span>
                  <span className="bg-[#00C9FF]/10 text-[#00C9FF] px-3 py-1 rounded-full text-sm font-medium">All Major Brands</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {forkliftParts.map((part, index) => (
                  <Card key={index} className="hover:shadow-xl transition-all border-2 hover:border-[#00C9FF]">
                    <CardContent className="p-6">
                      <div className="bg-gradient-to-br from-[#00C9FF]/10 to-[#00C9FF]/5 p-4 rounded-lg mb-4 flex items-center justify-center">
                        <Package className="h-12 w-12 text-[#00C9FF]" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">{part.name}</h3>
                      <p className="text-sm text-gray-600 mb-4">{part.desc}</p>
                      <div className="flex gap-2">
                        <Button 
                          onClick={() => handleWhatsAppEnquiry(part.name)}
                          className="flex-1 bg-[#25D366] hover:bg-[#20BD5A] text-white"
                          size="sm"
                        >
                          <MessageCircle className="mr-2 h-4 w-4" />
                          WhatsApp
                        </Button>
                        <Button 
                          variant="outline"
                          className="border-[#00C9FF] text-[#00C9FF] hover:bg-[#00C9FF] hover:text-white"
                          size="sm"
                        >
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Can't Find What You're Looking For?</h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Send us your machine model and part requirement. We'll help identify the correct part and check availability.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => handleWhatsAppEnquiry('Custom Part')}
              size="lg"
              className="bg-[#25D366] hover:bg-[#20BD5A] text-white"
            >
              <MessageCircle className="mr-2 h-5 w-5" />
              WhatsApp Us
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-[#00C9FF] text-[#00C9FF] hover:bg-[#00C9FF] hover:text-white"
              onClick={() => window.location.href = '/contact'}
            >
              Send Enquiry
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;
